import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createLostItem, createFoundItem, uploadItemImage, updateLostItem, updateFoundItem } from '../services/itemService';
import { ItemCategory, ItemStatus } from '../types';
import { CATEGORIES, LOCATIONS, inputClass, labelClass, capitalize } from '../constants';
import ImageUpload from '../components/ImageUpload';

type ItemType = 'lost' | 'found';

const CONFIG = {
  lost: {
    title: 'Report Lost Item',
    description: "Fill out the form below to report an item you've lost. We'll notify you if someone finds it.",
    dateLabel: 'Date Lost',
    locationLabel: 'Last Seen Location',
    submitText: 'Report Lost Item',
    cancelLink: '/items/lost'
  },
  found: {
    title: 'Report Found Item',
    description: "Fill out the form below to report an item you've found. We'll help reunite it with its owner.",
    dateLabel: 'Date Found',
    locationLabel: 'Where Found',
    submitText: 'Report Found Item',
    cancelLink: '/items/found'
  }
};

const ReportItemPage: React.FC = () => {
  const { type } = useParams<{ type: ItemType }>();
  const itemType: ItemType = type === 'found' ? 'found' : 'lost';
  const config = CONFIG[itemType];

  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '' as ItemCategory,
    location: '',
    date: '',
    phone: '',
    turnedInToOffice: false,
    officeLocation: ''
  });

  const updateField = <K extends keyof typeof formData>(key: K, value: typeof formData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    setLoading(true);
    try {
      const baseData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        location: formData.location,
        dateReported: new Date(),
        status: 'active' as ItemStatus,
        claimed: false
      };

      let itemId: string;
      if (itemType === 'lost') {
        itemId = await createLostItem({
          ...baseData,
          dateLost: new Date(formData.date),
          reportedBy: currentUser.uid,
          reporterEmail: currentUser.email || '',
          reporterPhone: formData.phone
        });
        if (imageFile) {
          const imageUrl = await uploadItemImage(imageFile, itemId);
          await updateLostItem(itemId, { imageUrl });
        }
      } else {
        itemId = await createFoundItem({
          ...baseData,
          dateFound: new Date(formData.date),
          foundBy: currentUser.uid,
          finderEmail: currentUser.email || '',
          finderPhone: formData.phone,
          turnedInToOffice: formData.turnedInToOffice,
          ...(formData.turnedInToOffice && formData.officeLocation ? { officeLocation: formData.officeLocation } : {})
        });
        if (imageFile) {
          const imageUrl = await uploadItemImage(imageFile, itemId);
          await updateFoundItem(itemId, { imageUrl });
        }
      }

      alert(`${capitalize(itemType)} item reported successfully!`);
      navigate(`/items/${itemType}`);
    } catch (error) {
      console.error(`Error reporting ${itemType} item:`, error);
      alert('Failed to report item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">{config.title}</h1>
            <p className="mt-2 text-gray-600">{config.description}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="title" className={labelClass}>Item Name *</label>
              <input
                type="text"
                id="title"
                required
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                className={inputClass}
                placeholder="e.g., Blue backpack, iPhone 13, Math textbook"
              />
            </div>

            <div>
              <label htmlFor="description" className={labelClass}>Description *</label>
              <textarea
                id="description"
                required
                rows={4}
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
                className={inputClass}
                placeholder="Provide detailed description including brand, color, size, distinctive features..."
              />
            </div>

            <div>
              <label htmlFor="category" className={labelClass}>Category *</label>
              <select
                id="category"
                required
                value={formData.category}
                onChange={(e) => updateField('category', e.target.value as ItemCategory)}
                className={inputClass}
              >
                <option value="">Select a category</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>{capitalize(cat)}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="location" className={labelClass}>{config.locationLabel}</label>
              <select
                id="location"
                value={formData.location}
                onChange={(e) => updateField('location', e.target.value)}
                className={inputClass}
              >
                <option value="">Select a location</option>
                {LOCATIONS.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="date" className={labelClass}>{config.dateLabel} *</label>
              <input
                type="date"
                id="date"
                required
                value={formData.date}
                onChange={(e) => updateField('date', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className={inputClass}
              />
            </div>

            {itemType === 'found' && (
              <>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="turnedInToOffice"
                    checked={formData.turnedInToOffice}
                    onChange={(e) => updateField('turnedInToOffice', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="turnedInToOffice" className="ml-2 block text-sm text-gray-700">
                    Item has been turned in to an office
                  </label>
                </div>
                {formData.turnedInToOffice && (
                  <div>
                    <label htmlFor="officeLocation" className={labelClass}>Office Location *</label>
                    <input
                      type="text"
                      id="officeLocation"
                      required
                      value={formData.officeLocation}
                      onChange={(e) => updateField('officeLocation', e.target.value)}
                      className={inputClass}
                      placeholder="e.g., Main Office, Room 101"
                    />
                  </div>
                )}
              </>
            )}

            <div>
              <label htmlFor="phone" className={labelClass}>Contact Phone (Optional)</label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className={inputClass}
                placeholder="(555) 123-4567"
              />
            </div>

            <ImageUpload
              preview={imagePreview}
              onFileChange={handleImageChange}
              onClear={() => { setImageFile(null); setImagePreview(''); }}
            />

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Submitting...' : config.submitText}
              </button>
              <button
                type="button"
                onClick={() => navigate(config.cancelLink)}
                className="px-6 py-3 border border-gray-300 rounded-md font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReportItemPage;